/** Logical admin endpoints behind the two Cognito-authenticated gateway routes.
 * This allowlist is shared by the browser client and backend dispatch. Privileged
 * provisioning/reporting/scheduling routes keep their dedicated integrations. */
export const ADMIN_CONSOLIDATED_ROUTE_KEYS = [
    "GET /orgs",
    "GET /orgs/{org}",
    "GET /orgs/{org}/search",
    "GET /orgs/{org}/identity",
    "GET /orgs/{org}/sending-identity",
    "GET /orgs/{org}/setup",
    "GET /orgs/{org}/lists",
    "POST /lists",
    "POST /lists/visibility",
    "GET /orgs/{org}/campaigns",
    "GET /orgs/{org}/campaigns/{id}",
    "GET /orgs/{org}/campaigns/{id}/content",
    "POST /campaigns",
    "GET /orgs/{org}/schedules",
    "POST /campaigns/lifecycle",
    "POST /campaigns/send-now",
    "POST /campaigns/content",
    "POST /campaigns/test-send",
    "GET /orgs/{org}/templates",
    "GET /orgs/{org}/templates/{id}",
    "POST /templates",
    "GET /orgs/{org}/merge-tags",
    "POST /merge-tags",
    "POST /merge-tags/delete",
    "GET /orgs/{org}/api-keys",
    "POST /api-keys",
    "POST /api-keys/revoke",
    "POST /api-keys/verify",
    "GET /orgs/{org}/webhook-secret",
    "POST /orgs/webhook-secret",
    "GET /orgs/{org}/series",
    "GET /orgs/{org}/series/{id}",
    "POST /series",
    "GET /orgs/{org}/feeds",
    "POST /feeds",
    "GET /orgs/{org}/segments",
    "POST /segments",
    "GET /orgs/{org}/segments/{segment}/members",
    "POST /segments/members",
    "GET /orgs/{org}/drip-sequences",
    "POST /drip-sequences",
    "POST /drip-sequences/enroll",
    "POST /subscribers/suppress",
    "POST /subscribers/unsubscribe",
    "GET /orgs/{org}/subscribers",
    "GET /orgs/{org}/subscribers/{sub}/timeline",
    "GET /orgs/{org}/subscribers/{sub}",
    "POST /subscribers/attributes",
    "POST /subscribers/subscription",
    "GET /orgs/{org}/suppressions",
    "GET /orgs/{org}/suppression/check",
    "POST /subscribers/unsuppress",
    "POST /orgs/{org}/import",
    "GET /orgs/{org}/health",
    "GET /orgs/{org}/team",
    "POST /team",
    "GET /orgs/{org}/export",
    "POST /orgs/{org}/import/preview",
    "POST /orgs/{org}/import/mapped",
    "POST /orgs/{org}/import/suppression",
    "POST /orgs/{org}/import/segment",
    "POST /orgs/{org}/import/upload-url",
    "POST /orgs/{org}/import/upload-preview",
    "POST /orgs/{org}/import/async",
    "GET /orgs/{org}/import/mappings",
    "POST /orgs/{org}/import/mappings",
    "GET /orgs/{org}/import/batches",
    "GET /orgs/{org}/audit",
    "POST /privacy",
    "POST /orgs/branding",
    "POST /orgs/settings",
    "GET /orgs/{org}/customer-sync",
    "POST /orgs/customer-sync",
    "GET /orgs/{org}/reengagement",
    "POST /orgs/reengagement",
    "GET /orgs/{org}/alerts",
    "POST /orgs/alerts",
    "POST /lists/presentation",
];
export const ADMIN_API_PREFIX = "/admin";
export const ADMIN_GATEWAY_PATH = "/admin/{proxy+}";
/** Match literal segments and decode parameter values exactly once. */
export function matchHttpRoute(keys, method, path) {
    if (!path.startsWith("/") || /[?#\\\x00-\x1f\x7f]/.test(path))
        return undefined;
    const segments = path.split("/");
    // Prefer literal routes over otherwise matching parameter routes.
    for (const routeKey of [...keys].sort((a, b) => a.split("{").length - b.split("{").length)) {
        const [verb, template] = routeKey.split(" ");
        if (verb !== method)
            continue;
        const expected = template.split("/");
        if (expected.length !== segments.length)
            continue;
        const pathParameters = {};
        let matches = true;
        for (let i = 0; i < expected.length; i++) {
            const name = expected[i].match(/^\{([a-zA-Z][a-zA-Z0-9_]*)\}$/)?.[1];
            if (!name) {
                if (expected[i] !== segments[i]) {
                    matches = false;
                    break;
                }
                continue;
            }
            try {
                const value = decodeURIComponent(segments[i]);
                if (!value || value === "." || value === ".." || /[\/\\\x00-\x1f\x7f]/.test(value) || /%[0-9a-f]{2}/i.test(value)) {
                    matches = false;
                    break;
                }
                pathParameters[name] = value;
            }
            catch {
                matches = false;
                break;
            }
        }
        if (matches)
            return { routeKey, pathParameters };
    }
    return undefined;
}
/** Preserve dedicated endpoints, query strings and opaque cursors. */
export function adminApiRequestPath(method, path) {
    const pathname = path.split("?")[0];
    return matchHttpRoute(ADMIN_CONSOLIDATED_ROUTE_KEYS, method, pathname) ? ADMIN_API_PREFIX + path : path;
}
//# sourceMappingURL=admin-routing.js.map